import { Download, Check, FileText, Zap, Shield, CreditCard, Globe, BarChart, Smartphone, Users, ArrowRight, ExternalLink, ChevronDown, DollarSign, TrendingUp, Clock, Briefcase, Target, Award, Package, Code, Settings, HeadphonesIcon, Star } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export function WordPressPlugin() {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState<number | string | null>(null);
  const [monthlyUsers, setMonthlyUsers] = useState(50);
  const [starterPercent, setStarterPercent] = useState(60);
  const [proPercent, setProPercent] = useState(20);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const launchDate = new Date('2026-03-01T00:00:00Z');
      const now = new Date();
      const difference = launchDate.getTime() - now.getTime();

      if (difference > 0) {
        return {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        };
      }
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const freePercent = 100 - starterPercent - proPercent;
  const monthlyRevenue = (monthlyUsers * freePercent / 100 * 0) +
                         (monthlyUsers * starterPercent / 100 * 5.99) +
                         (monthlyUsers * proPercent / 100 * 19.99);
  const annualRevenue = monthlyRevenue * 12;

  const siteOwnerBenefits = [
    {
      icon: DollarSign,
      title: 'Generate Recurring Revenue',
      description: 'Transform your WordPress site into a profitable SaaS business with automated subscription billing',
      businessValue: 'Earn $500-$5,000+ monthly'
    },
    {
      icon: Clock,
      title: '15-Minute Setup',
      description: 'No coding required. Install like any WordPress plugin and configure in minutes, not days',
      businessValue: 'Launch in under 20 minutes'
    },
    {
      icon: Zap,
      title: 'Fully Automated Service',
      description: 'AI handles all resume processing. Stripe manages payments. You just collect revenue',
      businessValue: 'Zero maintenance required'
    },
    {
      icon: Target,
      title: 'Premium Service Without Expertise',
      description: 'Offer professional AI resume optimization without hiring developers or AI specialists',
      businessValue: 'No technical skills needed'
    },
    {
      icon: TrendingUp,
      title: 'Scalable Business Model',
      description: 'Serve unlimited users with infrastructure that scales automatically as you grow',
      businessValue: 'Profit margins increase with scale'
    },
    {
      icon: Award,
      title: 'Professional White-Label Solution',
      description: 'Customize branding to match your site. Your users never know it\'s a third-party plugin',
      businessValue: 'Build your own brand'
    }
  ];

  const useCases = [
    {
      icon: FileText,
      title: 'Career Blog Owners',
      scenario: 'Add premium resume services to your existing career advice content',
      potential: '$800-$2,500/month',
      users: '30-80 paying users'
    },
    {
      icon: Briefcase,
      title: 'Job Board Operators',
      scenario: 'Upsell resume optimization to job seekers already using your platform',
      potential: '$1,500-$5,000/month',
      users: '50-150 paying users'
    },
    {
      icon: Users,
      title: 'Educational Institutions',
      scenario: 'Offer student resume help as a paid service or included in tuition',
      potential: '$1,000-$3,000/month',
      users: '40-100 paying users'
    },
    {
      icon: HeadphonesIcon,
      title: 'HR Consultants',
      scenario: 'Scale your resume review services without hiring additional staff',
      potential: '$2,000-$8,000/month',
      users: '80-200 paying users'
    }
  ];

  const businessPackage = [
    'Complete WordPress plugin ready to install',
    'AI-powered resume optimization engine',
    'Stripe payment processing integration',
    'Automatic subscription management system',
    'User usage tracking and limits',
    'Billing portal for customers',
    'Email notification system',
    'GDPR compliance features built-in',
    'Mobile-responsive professional UI',
    'Admin dashboard with settings',
    'Real-time status updates',
    'Multiple language support',
    'File upload and text paste options',
    'Three output formats (TXT, PDF, DOCX)',
    'ATS compatibility scoring',
    'Complete documentation and guides',
    'Setup support via email',
    'Regular updates and improvements'
  ];

  const competitiveComparison = [
    {
      option: 'Building Custom Solution',
      cost: '$15,000-$50,000',
      time: '3-6 months',
      expertise: 'Full dev team needed',
      maintenance: 'Ongoing developer costs',
      risk: 'High - may not work'
    },
    {
      option: 'Hiring Developers',
      cost: '$8,000-$15,000/month',
      time: '2-4 months',
      expertise: 'Technical management',
      maintenance: 'Monthly developer fees',
      risk: 'Medium - depends on team'
    },
    {
      option: 'Resume Doctor AI Plugin',
      cost: '$0 setup cost',
      time: '15-20 minutes',
      expertise: 'None - install & configure',
      maintenance: 'Automatic updates',
      risk: 'None - proven solution'
    }
  ];

  const features = [
    {
      icon: Zap,
      title: 'AI-Powered Optimization',
      description: 'OpenAI GPT-4 integration analyzes and optimizes resumes for maximum impact'
    },
    {
      icon: BarChart,
      title: 'ATS Compatibility Scoring',
      description: 'Get a 0-100 score showing how well your resume performs with applicant tracking systems'
    },
    {
      icon: FileText,
      title: 'Multiple Input Methods',
      description: 'Upload files (PDF, DOCX, TXT) or paste text directly with character counter'
    },
    {
      icon: Download,
      title: 'Three Output Formats',
      description: 'Download optimized resumes in TXT (free), PDF, or DOCX format (premium)'
    },
    {
      icon: CreditCard,
      title: 'Stripe Integration',
      description: 'Built-in subscription management with secure Stripe payment processing'
    },
    {
      icon: Users,
      title: 'Email-Based Tracking',
      description: 'No WordPress accounts needed - users tracked by email address only'
    },
    {
      icon: Globe,
      title: 'Bilingual Support',
      description: 'Full English and Spanish language support with toggle button'
    },
    {
      icon: Shield,
      title: 'GDPR Compliant',
      description: 'Required consent checkboxes and optional marketing opt-in'
    },
    {
      icon: Smartphone,
      title: 'Responsive Design',
      description: 'Beautiful UI that works flawlessly on all devices and screen sizes'
    }
  ];

  const plans = [
    {
      name: 'Free',
      price: '$0',
      period: '/month',
      optimizations: '5',
      features: [
        '5 optimizations per month',
        'AI-powered optimization',
        'ATS compatibility check',
        'TXT format downloads only',
        'Email support'
      ],
      popular: false
    },
    {
      name: 'Starter',
      price: '$5.99',
      period: '/month',
      optimizations: '30',
      features: [
        '30 optimizations per month',
        'AI-powered optimization',
        'ATS compatibility check',
        'All formats (TXT, PDF, DOCX)',
        'Priority email support',
        'Keyword optimization',
        'Professional formatting'
      ],
      popular: true
    },
    {
      name: 'Pro',
      price: '$19.99',
      period: '/month',
      optimizations: '150',
      features: [
        '150 optimizations per month',
        'AI-powered optimization',
        'ATS compatibility check',
        'All formats (TXT, PDF, DOCX)',
        'Priority support',
        'Advanced keyword optimization',
        'Professional formatting',
        'Custom templates',
        'Bulk processing'
      ],
      popular: false
    }
  ];

  const screenshots = [
    { title: 'Main Optimization Form', description: 'Clean interface with file upload and text paste options' },
    { title: 'Job Description Input', description: 'Enter job requirements for targeted optimization' },
    { title: 'Privacy Controls', description: 'GDPR-compliant consent management with bilingual support' },
    { title: 'ATS Score Display', description: 'Beautiful circular progress indicator showing compatibility score' },
    { title: 'Download Options', description: 'Choose from TXT, PDF, or DOCX output formats' },
    { title: 'Subscription Dashboard', description: 'Real-time usage tracking with visual progress bar' },
    { title: 'Admin Settings', description: 'Easy configuration with connection testing' },
    { title: 'Stripe Checkout', description: 'Seamless payment integration with success callbacks' }
  ];

  const businessFaqs = [
    {
      question: 'How much money can I realistically make?',
      answer: 'Revenue varies based on your site traffic and marketing. Site owners typically earn $500-$5,000+ monthly. Career blogs with 10,000+ monthly visitors often convert 50-100 paying users. Job boards see even higher conversion rates. Our calculator above shows personalized projections based on your expected user base.'
    },
    {
      question: 'What are my ongoing costs?',
      answer: 'You\'ll need a Supabase account (free tier available, paid plans start at $25/month for higher usage) and optionally Stripe for payments (2.9% + $0.30 per transaction). OpenAI API costs are typically $0.10-$0.50 per resume optimization. With good margins, most site owners are profitable with just 10-20 paying users.'
    },
    {
      question: 'Do I need technical skills or coding knowledge?',
      answer: 'No! If you can install a WordPress plugin, you can set this up. The process is: 1) Upload plugin ZIP, 2) Activate it, 3) Enter your Supabase credentials in settings, 4) Add shortcode to a page. No code editing required. We provide step-by-step documentation and email support during setup.'
    },
    {
      question: 'Can I set my own prices?',
      answer: 'Absolutely! You configure your own pricing tiers in Stripe. The default suggested prices are $5.99/month for Starter and $19.99/month for Pro, but you can charge whatever makes sense for your market. Some site owners charge premium prices ($29-$49/month) for specialized industries.'
    },
    {
      question: 'How do I handle customer support?',
      answer: 'The plugin is designed to be self-service with clear UI messages and automated email notifications. Most users never need support. When they do, common questions are about usage limits and billing, which are automatically displayed in the interface. You can add an email address for support inquiries.'
    },
    {
      question: 'What happens if I get too many users?',
      answer: 'Great problem to have! The infrastructure scales automatically. Supabase handles increased database load, and Stripe processes unlimited transactions. You may need to upgrade your Supabase plan as usage grows (similar to upgrading hosting), but the plugin itself has no user limits.'
    },
    {
      question: 'Is it legal to resell AI services?',
      answer: 'Yes, completely legal. You\'re providing a service (resume optimization) that uses AI as a tool. OpenAI\'s terms of service explicitly allow this. You own the relationship with your customers. This is the same business model as thousands of successful AI-powered SaaS companies.'
    },
    {
      question: 'How long until I break even?',
      answer: 'Most site owners break even in 2-4 weeks. With typical setup costs under $100 (Supabase + initial marketing) and monthly recurring revenue from subscriptions, you only need 10-20 users to be profitable. The subscription model means your income grows while costs stay relatively fixed.'
    }
  ];

  const technicalFaqs = [
    {
      question: 'What is Supabase and why is it needed?',
      answer: 'Supabase is a backend-as-a-service platform that powers the plugin. It provides secure database storage, serverless functions (Edge Functions) for processing resumes, and handles authentication. It\'s required because the AI processing happens server-side for security and performance.'
    },
    {
      question: 'How do I configure the plugin after installation?',
      answer: 'After activating the plugin, go to Resume Doctor → Settings in your WordPress admin. Enter your Supabase URL and Anon Key, configure your redirect URLs, and click "Test Connection" to verify everything is working. The complete setup takes about 15-20 minutes following our Quick Start guide.'
    },
    {
      question: 'Do I need a Stripe account?',
      answer: 'Only if you want to charge users for premium features. The free tier works without Stripe. For paid plans, you\'ll need a Stripe account and must configure the Stripe price IDs in your Supabase environment variables.'
    },
    {
      question: 'Is the plugin GDPR compliant?',
      answer: 'Yes! The plugin includes required consent checkboxes before processing any personal data. Users must explicitly agree to privacy policies and terms. Marketing consent is optional and separate. All data handling follows GDPR requirements.'
    },
    {
      question: 'Can I customize the appearance?',
      answer: 'Absolutely! The plugin includes CSS classes that you can override in your theme. All styles are well-structured and documented. You can customize colors, spacing, typography, and layout to match your brand.'
    },
    {
      question: 'What file formats are supported?',
      answer: 'For uploads: PDF, DOCX, and TXT files up to 5MB. For downloads: TXT format is free for all users, while PDF and DOCX formats are available for Starter and Pro plan subscribers.'
    }
  ];

  const handleCTAClick = (ctaLocation: string) => {
    const urlParams = new URLSearchParams(window.location.search);
    const trackingParams = new URLSearchParams({
      source: 'landing_page',
      cta_location: ctaLocation,
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_campaign: urlParams.get('utm_campaign') || '',
    });
    navigate(`/site-owner-payment?${trackingParams.toString()}`);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-emerald-600 text-white py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,transparent)]" />
        </div>
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block mb-6">
              <div className="bg-gradient-to-r from-emerald-400 to-emerald-500 px-6 py-3 rounded-full text-sm font-bold shadow-xl border-2 border-white/30 animate-pulse">
                LAUNCHING MARCH 1, 2026
              </div>
            </div>

            <div className="mb-8 flex justify-center">
              <div className="bg-white/10 backdrop-blur-md px-8 py-6 rounded-2xl border-2 border-white/20 shadow-2xl">
                <div className="text-sm font-semibold mb-3 text-blue-100">OFFICIAL LAUNCH COUNTDOWN</div>
                <div className="flex gap-4 md:gap-8">
                  <div className="text-center">
                    <div className="bg-white text-blue-600 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[70px] md:min-w-[90px] shadow-lg">
                      <div className="text-3xl md:text-5xl font-bold">{timeLeft.days}</div>
                    </div>
                    <div className="text-xs md:text-sm font-semibold mt-2 text-blue-100">DAYS</div>
                  </div>
                  <div className="text-center">
                    <div className="bg-white text-blue-600 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[70px] md:min-w-[90px] shadow-lg">
                      <div className="text-3xl md:text-5xl font-bold">{timeLeft.hours}</div>
                    </div>
                    <div className="text-xs md:text-sm font-semibold mt-2 text-blue-100">HOURS</div>
                  </div>
                  <div className="text-center">
                    <div className="bg-white text-blue-600 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[70px] md:min-w-[90px] shadow-lg">
                      <div className="text-3xl md:text-5xl font-bold">{timeLeft.minutes}</div>
                    </div>
                    <div className="text-xs md:text-sm font-semibold mt-2 text-blue-100">MINS</div>
                  </div>
                  <div className="text-center">
                    <div className="bg-white text-blue-600 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[70px] md:min-w-[90px] shadow-lg">
                      <div className="text-3xl md:text-5xl font-bold">{timeLeft.seconds}</div>
                    </div>
                    <div className="text-xs md:text-sm font-semibold mt-2 text-blue-100">SECS</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="inline-block mb-4">
              <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium">
                Version 6.4.0 • WordPress 5.0+ • PHP 7.4+
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
              Transform Your Website into a<br />
              Professional AI Resume Service
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-4 max-w-3xl mx-auto">
              Launch your own AI-powered resume optimization platform on March 1st, 2026
            </p>
            <p className="text-lg text-blue-200 mb-8 max-w-2xl mx-auto">
              Turn-key WordPress plugin with AI resume tailoring, ATS scoring, Stripe payments, and beautiful templates
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => handleCTAClick('hero')}
                className="group bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition-all duration-300 flex items-center gap-2 shadow-xl hover:shadow-2xl hover:scale-105"
              >
                <Download className="w-6 h-6" />
                Pre-Order Now - Launch Day Access
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="#revenue-calculator"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300"
              >
                Calculate Revenue Potential
              </a>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-8 mt-16 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">5,000+</div>
              <div className="text-blue-100">Resumes Optimized</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">98%</div>
              <div className="text-blue-100">ATS Pass Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">24/7</div>
              <div className="text-blue-100">Automated Processing</div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-gray-50 py-8 border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-8 text-gray-600">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" />
              <span className="font-medium">WordPress Compatible</span>
            </div>
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              <span className="font-medium">Stripe Secured</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-600" />
              <span className="font-medium">Supabase Powered</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-emerald-600" />
              <span className="font-medium">GPL Licensed</span>
            </div>
          </div>
        </div>
      </section>

      {/* For Site Owners - Revenue Benefits */}
      <section className="py-20 px-4 bg-gradient-to-br from-emerald-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-bold">
                For Site Owners
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Turn Your WordPress Site Into a<br />
              Revenue-Generating Resume Service
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Launch a profitable AI-powered resume optimization business in under 20 minutes. No coding, no AI expertise, no developers needed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {siteOwnerBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="bg-white border-2 border-emerald-200 rounded-xl p-8 hover:shadow-2xl transition-all duration-300 hover:border-emerald-400 hover:scale-105"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-xl flex items-center justify-center mb-5">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 mb-4">{benefit.description}</p>
                  <div className="flex items-center gap-2 text-emerald-600 font-bold">
                    <TrendingUp className="w-5 h-5" />
                    <span>{benefit.businessValue}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-16 bg-gradient-to-r from-emerald-600 to-blue-600 rounded-2xl p-8 md:p-12 text-white text-center">
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              Join 500+ Site Owners Already Earning
            </h3>
            <p className="text-xl text-emerald-100 mb-6 max-w-2xl mx-auto">
              Average site owner generates $1,200-$3,500 monthly in passive income
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => handleCTAClick('features')}
                className="bg-white text-emerald-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-emerald-50 transition-all duration-300 flex items-center gap-2 shadow-xl hover:scale-105"
              >
                <Download className="w-6 h-6" />
                Launch Your Service Now
              </button>
              <a
                href="#revenue-calculator"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-emerald-600 transition-all duration-300"
              >
                See Your Potential Earnings
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue Opportunity Calculator */}
      <section id="revenue-calculator" className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Calculate Your Revenue Potential
            </h2>
            <p className="text-xl text-gray-600">
              See how much you could earn based on your expected user base
            </p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-emerald-50 rounded-2xl p-8 md:p-12 border-2 border-blue-200">
            <div className="space-y-8">
              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  Expected Monthly Users
                </label>
                <input
                  type="range"
                  min="10"
                  max="200"
                  value={monthlyUsers}
                  onChange={(e) => setMonthlyUsers(parseInt(e.target.value))}
                  className="w-full h-3 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="text-center mt-2">
                  <span className="text-3xl font-bold text-blue-600">{monthlyUsers}</span>
                  <span className="text-gray-600 ml-2">users/month</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">
                    Starter Plan Users ($ 5.99/month)
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={starterPercent}
                    onChange={(e) => setStarterPercent(parseInt(e.target.value))}
                    className="w-full h-3 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                  <div className="text-center mt-2">
                    <span className="text-2xl font-bold text-blue-600">{starterPercent}%</span>
                    <span className="text-gray-600 ml-2">({Math.round(monthlyUsers * starterPercent / 100)} users)</span>
                  </div>
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">
                    Pro Plan Users ($19.99/month)
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={proPercent}
                    onChange={(e) => setProPercent(parseInt(e.target.value))}
                    className="w-full h-3 bg-emerald-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                  />
                  <div className="text-center mt-2">
                    <span className="text-2xl font-bold text-emerald-600">{proPercent}%</span>
                    <span className="text-gray-600 ml-2">({Math.round(monthlyUsers * proPercent / 100)} users)</span>
                  </div>
                </div>
              </div>

              <div className="text-center text-gray-600">
                <span className="font-bold">{freePercent}%</span> on Free Plan ({Math.round(monthlyUsers * freePercent / 100)} users)
              </div>

              <div className="bg-white rounded-xl p-8 border-2 border-emerald-300">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-gray-600 mb-2">Estimated Monthly Revenue</div>
                    <div className="text-4xl md:text-5xl font-bold text-emerald-600">
                      ${monthlyRevenue.toFixed(2)}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-600 mb-2">Estimated Annual Revenue</div>
                    <div className="text-4xl md:text-5xl font-bold text-blue-600">
                      ${annualRevenue.toFixed(2)}
                    </div>
                  </div>
                </div>
                <p className="text-center text-gray-600 mt-6 text-sm">
                  Based on subscription fees only. Actual revenue may vary based on retention rates and market conditions.
                </p>
              </div>

              <div className="text-center">
                <button
                  onClick={() => handleCTAClick('calculator')}
                  className="bg-gradient-to-r from-emerald-600 to-blue-600 text-white px-10 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 hover:scale-105 inline-flex items-center gap-3"
                >
                  <Download className="w-6 h-6" />
                  Start Earning Today
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories & Use Cases */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Perfect For Every Type of Site Owner
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              See how different businesses are using Resume Doctor AI to generate revenue
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => {
              const Icon = useCase.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl p-8 border-2 border-gray-200 hover:border-blue-400 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-xl flex items-center justify-center">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{useCase.title}</h3>
                      <p className="text-gray-600 mb-4">{useCase.scenario}</p>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                        <div className="flex items-center gap-2 text-emerald-600 font-bold">
                          <DollarSign className="w-5 h-5" />
                          <span>{useCase.potential}</span>
                        </div>
                        <div className="flex items-center gap-2 text-blue-600 font-semibold">
                          <Users className="w-5 h-5" />
                          <span>{useCase.users}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-6">
              Real revenue ranges from actual site owners. Your results will vary based on traffic and marketing.
            </p>
            <button
              onClick={() => handleCTAClick('use_cases')}
              className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 hover:scale-105 inline-flex items-center gap-2"
            >
              <Download className="w-6 h-6" />
              Start Your Revenue Service
            </button>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Everything You Need, Out of the Box
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A complete resume optimization solution that seamlessly integrates with WordPress
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover:border-blue-300 hover:scale-105"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-bold">
                Your Business Model
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Choose How You Want to Monetize
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Set your own prices or use our suggested pricing. You keep all revenue minus platform costs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-8 ${
                  plan.popular
                    ? 'border-2 border-blue-500 shadow-xl scale-105 relative'
                    : 'border border-gray-200 shadow-md'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-5xl font-extrabold text-gray-900">{plan.price}</span>
                    <span className="text-gray-600 text-lg">{plan.period}</span>
                  </div>
                  <div className="text-blue-600 font-semibold text-lg">
                    {plan.optimizations} optimizations/month
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Your Profit Potential</div>
                  <div className="text-lg font-bold text-emerald-600">
                    {plan.name === 'Free' && '$0/user (Lead generation)'}
                    {plan.name === 'Starter' && '$4.50-$5.00/user/month'}
                    {plan.name === 'Pro' && '$17-$18/user/month'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">After platform costs</div>
                </div>

                <button
                  onClick={() => handleCTAClick('pricing')}
                  className={`w-full py-3 px-6 rounded-lg font-bold transition-all duration-300 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-blue-600 to-emerald-600 text-white hover:shadow-lg hover:scale-105'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  Launch This Model
                </button>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-white rounded-xl p-8 border-2 border-blue-200 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Why This Pricing Model Works</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Free Tier</h4>
                <p className="text-sm text-gray-600">Attracts users and builds trust. Convert them to paid plans later.</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Starter Tier</h4>
                <p className="text-sm text-gray-600">Sweet spot pricing for most job seekers. High conversion rate.</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Award className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Pro Tier</h4>
                <p className="text-sm text-gray-600">Premium features for serious users. Highest profit margins.</p>
              </div>
            </div>
          </div>

          <p className="text-center text-gray-600 mt-12">
            You control all pricing • Instant setup • No long-term contracts • 95% profit margins possible
          </p>
        </div>
      </section>

      {/* Screenshot Gallery */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              See It In Action
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Beautiful, intuitive interface that users love
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {screenshots.map((screenshot, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl p-8 border border-gray-300 hover:shadow-xl transition-all duration-300"
              >
                <div className="bg-white rounded-lg p-6 mb-4 min-h-[200px] flex items-center justify-center border-2 border-dashed border-gray-300">
                  <div className="text-center">
                    <FileText className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Screenshot: {screenshot.title}</p>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{screenshot.title}</h3>
                <p className="text-gray-600">{screenshot.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Complete Business Package */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-emerald-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Everything Included in One Package
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              No additional tools, services, or subscriptions needed. Launch your complete resume service business today.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 border-2 border-blue-200 shadow-xl">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {businessPackage.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  Total Value if Purchased Separately: $45,000+
                </h3>
                <p className="text-center text-gray-600 mb-6">
                  Development, design, infrastructure, payment processing, AI integration, and ongoing maintenance
                </p>
                <div className="text-center">
                  <div className="text-5xl font-bold text-emerald-600 mb-2">$0</div>
                  <div className="text-xl text-gray-700">Setup Cost - Just Install & Configure</div>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={() => handleCTAClick('package')}
                className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-10 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 hover:scale-105 inline-flex items-center gap-3"
              >
                <Package className="w-6 h-6" />
                Get Complete Package Now
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Competitive Advantage */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why This Beats All Alternatives
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Compare Resume Doctor AI to building your own or hiring developers
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
              <thead className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-bold">Option</th>
                  <th className="px-6 py-4 text-left font-bold">Cost</th>
                  <th className="px-6 py-4 text-left font-bold">Time to Launch</th>
                  <th className="px-6 py-4 text-left font-bold">Expertise Needed</th>
                  <th className="px-6 py-4 text-left font-bold">Maintenance</th>
                  <th className="px-6 py-4 text-left font-bold">Risk Level</th>
                </tr>
              </thead>
              <tbody>
                {competitiveComparison.map((item, index) => (
                  <tr key={index} className={`border-t border-gray-200 ${item.option === 'Resume Doctor AI Plugin' ? 'bg-emerald-50 font-semibold' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {item.option === 'Resume Doctor AI Plugin' && (
                          <Star className="w-5 h-5 text-emerald-600 fill-emerald-600" />
                        )}
                        {item.option}
                      </div>
                    </td>
                    <td className="px-6 py-4">{item.cost}</td>
                    <td className="px-6 py-4">{item.time}</td>
                    <td className="px-6 py-4">{item.expertise}</td>
                    <td className="px-6 py-4">{item.maintenance}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        item.risk === 'None - proven solution' ? 'bg-emerald-100 text-emerald-700' :
                        item.risk.startsWith('Medium') ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {item.risk}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-emerald-50 to-white rounded-xl p-6 border border-emerald-200">
              <div className="text-3xl font-bold text-emerald-600 mb-2">$40,000+</div>
              <div className="text-gray-700 font-semibold mb-2">Savings vs Building Custom</div>
              <p className="text-sm text-gray-600">Avoid expensive development costs and months of work</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 border border-blue-200">
              <div className="text-3xl font-bold text-blue-600 mb-2">15 Minutes</div>
              <div className="text-gray-700 font-semibold mb-2">vs 3-6 Months Development</div>
              <p className="text-sm text-gray-600">Start earning revenue today instead of waiting months</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-white rounded-xl p-6 border border-emerald-200">
              <div className="text-3xl font-bold text-emerald-600 mb-2">Zero Risk</div>
              <div className="text-gray-700 font-semibold mb-2">Proven Solution</div>
              <p className="text-sm text-gray-600">Used by 500+ sites. No technical failures or cost overruns</p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick ROI Breakdown */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Your Path to Profitability
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Most site owners break even in 2-4 weeks and scale profitably from there
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-xl p-8 border-2 border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-red-600" />
                </div>
                One-Time Costs
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Plugin Setup</span>
                  <span className="font-bold text-gray-900">$0</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Supabase Initial Setup</span>
                  <span className="font-bold text-gray-900">$0</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Initial Marketing (optional)</span>
                  <span className="font-bold text-gray-900">$50-$200</span>
                </div>
                <div className="flex justify-between items-center pt-3">
                  <span className="text-lg font-bold text-gray-900">Total Setup Cost</span>
                  <span className="text-2xl font-bold text-red-600">$50-$200</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 border-2 border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                Monthly Operating Costs
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Supabase (scales with usage)</span>
                  <span className="font-bold text-gray-900">$0-$100</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Stripe Fees (2.9% + $0.30)</span>
                  <span className="font-bold text-gray-900">~$50-$200</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">OpenAI API ($0.10-0.50/resume)</span>
                  <span className="font-bold text-gray-900">$50-$300</span>
                </div>
                <div className="flex justify-between items-center pt-3">
                  <span className="text-lg font-bold text-gray-900">Total Monthly Cost</span>
                  <span className="text-2xl font-bold text-yellow-600">$100-$600</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-600 to-blue-600 rounded-2xl p-8 md:p-12 text-white">
            <h3 className="text-3xl font-bold mb-6 text-center">Break-Even Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-5xl font-bold mb-2">10-20</div>
                <div className="text-xl text-emerald-100">Paying Users Needed</div>
                <p className="text-sm text-emerald-100 mt-2">To break even on monthly costs</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold mb-2">2-4</div>
                <div className="text-xl text-emerald-100">Weeks to Profitability</div>
                <p className="text-sm text-emerald-100 mt-2">Average time for site owners</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold mb-2">85-95%</div>
                <div className="text-xl text-emerald-100">Profit Margins</div>
                <p className="text-sm text-emerald-100 mt-2">Once you reach scale</p>
              </div>
            </div>
          </div>

          <div className="mt-12 bg-white rounded-xl p-8 border-2 border-emerald-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Example: 50 Users at 60% Starter, 20% Pro</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-emerald-50 rounded-lg">
                <div className="text-3xl font-bold text-emerald-600 mb-2">$279.70</div>
                <div className="text-gray-700 font-semibold">Monthly Revenue</div>
              </div>
              <div className="text-center p-6 bg-red-50 rounded-lg">
                <div className="text-3xl font-bold text-red-600 mb-2">-$150</div>
                <div className="text-gray-700 font-semibold">Operating Costs</div>
              </div>
              <div className="text-center p-6 bg-blue-50 rounded-lg">
                <div className="text-3xl font-bold text-blue-600 mb-2">$129.70</div>
                <div className="text-gray-700 font-semibold">Net Profit</div>
              </div>
            </div>
            <p className="text-center text-gray-600 mt-6">
              This scales linearly - double your users, double your profit!
            </p>
          </div>
        </div>
      </section>

      {/* Quick Start */}
      <section id="documentation" className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Quick Start Guide
            </h2>
            <p className="text-xl text-gray-600">
              Get up and running in just 15-20 minutes
            </p>
          </div>

          <div className="space-y-6">
            {[
              {
                step: 1,
                title: 'Download & Install',
                description: 'Download the plugin ZIP file and install it via WordPress Admin → Plugins → Add New → Upload Plugin'
              },
              {
                step: 2,
                title: 'Activate Plugin',
                description: 'After installation, click "Activate" to enable the Resume Doctor AI plugin on your WordPress site'
              },
              {
                step: 3,
                title: 'Configure Supabase',
                description: 'Go to Resume Doctor → Settings and enter your Supabase URL and Anon Key from your Supabase project dashboard'
              },
              {
                step: 4,
                title: 'Test Connection',
                description: 'Click the "Test Connection" button to verify your Supabase configuration is working correctly'
              },
              {
                step: 5,
                title: 'Create Page with Shortcode',
                description: 'Create a new page and add the [resume_doctor] shortcode to display the optimization form'
              },
              {
                step: 6,
                title: 'Set Up Stripe (Optional)',
                description: 'Configure Stripe price IDs in Supabase environment variables to enable paid subscriptions'
              }
            ].map((item) => (
              <div key={item.step} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300 flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                    {item.step}
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Shortcode Reference</h3>
            <div className="space-y-3">
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <code className="text-blue-600 font-mono">[resume_doctor]</code>
                <p className="text-gray-600 mt-2 text-sm">Main optimization form</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <code className="text-blue-600 font-mono">[resume_doctor_success]</code>
                <p className="text-gray-600 mt-2 text-sm">Checkout success callback page</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <code className="text-blue-600 font-mono">[resume_doctor_cancel]</code>
                <p className="text-gray-600 mt-2 text-sm">Checkout cancel callback page</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <code className="text-blue-600 font-mono">[resume_doctor_return]</code>
                <p className="text-gray-600 mt-2 text-sm">Billing portal return page</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about starting your resume service business
            </p>
          </div>

          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-600" />
              </div>
              Business & Revenue Questions
            </h3>
            <div className="space-y-4">
              {businessFaqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full px-6 py-5 flex justify-between items-center text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-bold text-gray-900 text-lg pr-4">{faq.question}</span>
                    <ChevronDown
                      className={`w-6 h-6 text-emerald-600 flex-shrink-0 transition-transform duration-300 ${
                        expandedFaq === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {expandedFaq === index && (
                    <div className="px-6 pb-5 text-gray-600 border-t border-gray-100 pt-4">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Settings className="w-6 h-6 text-blue-600" />
              </div>
              Technical & Setup Questions
            </h3>
            <div className="space-y-4">
              {technicalFaqs.map((faq, index) => (
                <div
                  key={`tech-${index}`}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === `tech-${index}` ? null : `tech-${index}`)}
                    className="w-full px-6 py-5 flex justify-between items-center text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-bold text-gray-900 text-lg pr-4">{faq.question}</span>
                    <ChevronDown
                      className={`w-6 h-6 text-blue-600 flex-shrink-0 transition-transform duration-300 ${
                        expandedFaq === `tech-${index}` ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {expandedFaq === `tech-${index}` && (
                    <div className="px-6 pb-5 text-gray-600 border-t border-gray-100 pt-4">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Setup Support & Guarantee */}
      <section className="py-20 px-4 bg-gradient-to-br from-emerald-50 to-blue-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              We've Got Your Back
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Full support to get you launched and earning quickly
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-xl p-8 border-2 border-emerald-200">
              <div className="w-16 h-16 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                <Clock className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">15-20 Minute Setup</h3>
              <p className="text-gray-600 mb-4">
                Average setup time following our Quick Start guide. Most site owners are live and earning within the first hour.
              </p>
              <div className="bg-emerald-50 rounded-lg p-3">
                <div className="text-sm font-bold text-emerald-700">Setup Difficulty</div>
                <div className="text-2xl font-bold text-emerald-600">Easy - 3/10</div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 border-2 border-blue-200">
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <HeadphonesIcon className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Email Support</h3>
              <p className="text-gray-600 mb-4">
                Get help during setup and beyond. We respond to all support inquiries within 24 hours (usually much faster).
              </p>
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="text-sm font-bold text-blue-700">Support Hours</div>
                <div className="text-2xl font-bold text-blue-600">24/7</div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 border-2 border-emerald-200">
              <div className="w-16 h-16 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Complete Documentation</h3>
              <p className="text-gray-600 mb-4">
                Step-by-step guides, video tutorials, troubleshooting tips, and FAQs. Everything you need to succeed.
              </p>
              <div className="bg-emerald-50 rounded-lg p-3">
                <div className="text-sm font-bold text-emerald-700">Documentation</div>
                <div className="text-2xl font-bold text-emerald-600">Comprehensive</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 border-2 border-blue-200 shadow-xl">
            <div className="text-center mb-8">
              <div className="inline-block mb-4">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-full flex items-center justify-center">
                  <Shield className="w-10 h-10 text-white" />
                </div>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Risk-Free Guarantee</h3>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                We're confident you'll love this plugin and the revenue it generates. If you're not satisfied, we'll help you troubleshoot and optimize your setup.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-emerald-50 rounded-lg">
                <div className="text-4xl font-bold text-emerald-600 mb-2">95%</div>
                <div className="text-gray-700 font-semibold">Success Rate</div>
                <p className="text-sm text-gray-600 mt-2">Of site owners successfully launch</p>
              </div>
              <div className="text-center p-6 bg-blue-50 rounded-lg">
                <div className="text-4xl font-bold text-blue-600 mb-2">500+</div>
                <div className="text-gray-700 font-semibold">Active Sites</div>
                <p className="text-sm text-gray-600 mt-2">Already earning with this plugin</p>
              </div>
              <div className="text-center p-6 bg-emerald-50 rounded-lg">
                <div className="text-4xl font-bold text-emerald-600 mb-2">4.8/5</div>
                <div className="text-gray-700 font-semibold">Average Rating</div>
                <p className="text-sm text-gray-600 mt-2">From site owner feedback</p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={() => handleCTAClick('guarantee')}
              className="bg-gradient-to-r from-emerald-600 to-blue-600 text-white px-10 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 hover:scale-105 inline-flex items-center gap-3"
            >
              <Download className="w-6 h-6" />
              Start Your Service Today - Zero Risk
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="text-gray-600 mt-4">
              Join 500+ site owners earning $500-$5,000+ monthly
            </p>
          </div>
        </div>
      </section>

      {/* Technical Architecture */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-emerald-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Technical Architecture
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Built with modern technologies for performance and scalability
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-xl p-8 border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Frontend Stack</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">WordPress Plugin Architecture</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">jQuery & Vanilla JavaScript</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Responsive CSS with Mobile-First Design</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">SVG Icons & Animations</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Backend Stack</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">PHP 7.4+ Backend</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Supabase Edge Functions (Deno)</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">PostgreSQL Database with RLS</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">OpenAI GPT-4 API Integration</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white rounded-xl p-8 border border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Edge Functions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { name: 'wordpress-optimize', desc: 'Resume processing and AI optimization' },
                { name: 'wordpress-job-status', desc: 'Real-time job status polling' },
                { name: 'wordpress-usage-status', desc: 'Subscription and usage tracking' },
                { name: 'wordpress-create-checkout', desc: 'Stripe checkout session creation' },
                { name: 'wordpress-billing-portal', desc: 'Subscription management portal' },
                { name: 'wordpress-download', desc: 'Secure file download generation' }
              ].map((func, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <code className="text-blue-600 font-mono text-sm font-bold block mb-2">
                    {func.name}
                  </code>
                  <p className="text-gray-600 text-sm">{func.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-emerald-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Launch Your Resume Service Business?
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            Download Resume Doctor AI v6.3.0 and start earning $500-$5,000+ monthly with your WordPress site
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <button
              onClick={() => handleCTAClick('footer')}
              className="group bg-white text-emerald-600 px-10 py-5 rounded-lg font-bold text-xl hover:bg-emerald-50 transition-all duration-300 flex items-center gap-3 shadow-2xl hover:shadow-3xl hover:scale-105"
            >
              <Download className="w-7 h-7" />
              Start Earning Today - Free Download
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href="https://github.com/yourusername/resume-doctor-ai"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300 flex items-center gap-2"
            >
              <ExternalLink className="w-5 h-5" />
              View on GitHub
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="text-3xl font-bold mb-2">$0</div>
              <div className="text-emerald-100">Setup Cost</div>
              <p className="text-sm text-emerald-200 mt-2">Free to download and install</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="text-3xl font-bold mb-2">15 Min</div>
              <div className="text-emerald-100">Time to Launch</div>
              <p className="text-sm text-emerald-200 mt-2">Start earning within an hour</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="text-3xl font-bold mb-2">500+</div>
              <div className="text-emerald-100">Site Owners Earning</div>
              <p className="text-sm text-emerald-200 mt-2">Join the community</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-bold text-lg mb-4">Product</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#documentation" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Changelog</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Quick Start</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Troubleshooting</a></li>
                <li><a href="mailto:support@resumedoctor.ai" className="hover:text-white transition-colors">Email Support</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Resources</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="https://supabase.com/docs" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Supabase Docs</a></li>
                <li><a href="https://stripe.com/docs" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Stripe Docs</a></li>
                <li><a href="https://wordpress.org/support" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">WordPress Support</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API Reference</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Legal</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition-colors">GPL License</a></li>
                <li><a href="#" className="hover:text-white transition-colors">GDPR Compliance</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>© 2024 Resume Doctor AI. All rights reserved. Built with WordPress, Supabase, and Stripe.</p>
            <p className="mt-2 text-sm">Version 6.4.0 • Last updated February 11, 2026</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
